import { Reducers } from '@sda/enums/store';
import { PrivateUserMask, PublicUserMask } from '@sda/sdk/masks';
import { SDKFactory } from './helpers/sdk.factory';
import { SDK } from './helpers/sdk';
import type { PublicReview } from '@sda/interfaces/review';
import type { FetchDataType, FirestoreClientData } from './interfaces';
import type { Message } from '@sda/interfaces/message';
import type { Ticket } from '@sda/interfaces/ticket';
import type { PublicUser, PrivateUser, User } from '@sda/interfaces/user';
import type { HyperwalletAccount, HyperwalletAccountLog } from '@sda/interfaces/user/hyperwallet';

/**
 * SDK Class to interact with Firestore Users.
 */
export class Users extends SDK<PublicUser> {
  constructor(apiClient: FirestoreClientData) {
    super(apiClient, Reducers.USERS);

    this.collection = this.fsh.Schema.PublicUsers;

    this.PublicMap = PublicUserMask;

    this.PrivateMap = PrivateUserMask;
  }

  async getCombined(id: string): Promise<User & FetchDataType> {
    return this.getCombinedRecord<PrivateUser>(
      id,
      this.Private().collection,
    );
  }

  Private(): typeof ExtendedSDK {
    interface PrivateSDK extends SDK<PrivateUser> {
      HyperwalletAccounts: (id: string) => SDK<HyperwalletAccount>;
      HyperwalletAccountsLogs: (id: string) => SDK<HyperwalletAccountLog>;
      Messages: (id: string) => SDK<Message>;
      PurchasedTickets: (id: string) => SDK<Ticket>;
      Reviews: (id: string) => SDK<PublicReview>;
    }

    let ExtendedSDK = SDKFactory<PrivateUser>(this.fsh.clientData, this.fsh.Schema.PrivateUsers, { reducerName: Reducers.USERS }) as PrivateSDK;

    ExtendedSDK.HyperwalletAccounts = (id: string) => SDKFactory<HyperwalletAccount>(
      this.fsh.clientData,
      this.fsh.getCollection<HyperwalletAccount>(this.fsh.Schema.PrivateUsers, id, 'HyperwalletAccounts'),
      { reducerName: Reducers.HYPERWALLET_ACCOUNTS },
    );

    ExtendedSDK.HyperwalletAccountsLogs = (id: string) => SDKFactory<HyperwalletAccountLog>(
      this.fsh.clientData,
      this.fsh.getCollection<HyperwalletAccountLog>(this.fsh.Schema.PrivateUsers, id, 'HyperwalletAccountsLogs'),
      { reducerName: Reducers.HYPERWALLET_LOGS },
    );

    ExtendedSDK.Messages = (id: string) => SDKFactory<Message>(
      this.fsh.clientData,
      this.fsh.getCollection<Message>(this.fsh.Schema.PrivateUsers, id, 'Messages'),
      { reducerName: Reducers.MESSAGES },
    );

    ExtendedSDK.PurchasedTickets = (id: string) => SDKFactory<Ticket>(
      this.fsh.clientData,
      this.fsh.getCollection<Ticket>(this.fsh.Schema.PrivateUsers, id, 'PurchasedTickets'),
      { reducerName: Reducers.TICKETS },
    );

    ExtendedSDK.Reviews = (id: string) => SDKFactory<PublicReview>(
      this.fsh.clientData,
      this.fsh.getCollection<PublicReview>(this.fsh.Schema.PrivateUsers, id, 'Reviews'),
      { reducerName: Reducers.REVIEWS },
    );

    return ExtendedSDK;
  }
}
